























































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import Modal from '@/shared/components/Modal.vue';
import { maxLength, required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import VuelidateUtil from '@/shared/util/vuelidateUtil';
import Toast from '@/shared/services/toast';
import { IntentViewModel } from '@/features/bots/models/intents/intentViewModel';
import { UtteranceViewModel } from '@/features/bots/models/intents/utteranceViewModel';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import UtteranceList from '@/features/bots/components/designer/UtteranceList.vue';
import { NLP_ENABLED } from '@/store/app/featureTypes';
import { GET_INTENT, UPDATE_INTENT } from '@/store/intents/actionTypes';
import { SaveIntentViewModel } from '@/features/bots/models/intents/saveIntentViewModel';

@Component({
	mixins: [
		validationMixin,
	],
	components: {
		UtteranceList,
		LoadingSpinner,
		Modal
	},
	validations: {
		selectedIntentTitle: {
			required,
			maxLength: maxLength(50),
			intentNameValid: VuelidateUtil.intentName
		},
		selectedIntentUtterances: {
			required,
			$each: {
				text: {
					required,
					isUnique(value) {
						return !(this.$v.selectedIntentUtterances && this.$v.selectedIntentUtterances.$model.filter((utterance: any) => utterance.text === value).length > 1);
					}
				}
			}
		}
	}
})
export default class EditIntentModal extends Vue {
	@Getter public isWorkspaceFeatureEnabled!: (key: string) => boolean;
	@Action(GET_INTENT) public getIntent: (id: string) => Promise<IntentViewModel>;
	@Action(UPDATE_INTENT) public updateIntent: (request: { id: string, intent: SaveIntentViewModel }) => Promise<IntentViewModel>;
	@Prop({ required: true }) public intentId: string;
	public show: boolean = false;

	public isSaveInProgress: boolean = false;

	public selectedIntent: IntentViewModel;
	public selectedIntentId: string = '';
	public selectedIntentTitle: string = '';
	public selectedIntentUtterances: UtteranceViewModel[] = [];
	public loading: boolean = false;

	@Watch('intentId', { immediate: true })
	public async onIntentIdChange(id: string) {
		this.intentId = id;
	}

	public async showModal() {
		this.loading = true;
		this.show = true;
		await this.loadIntentWithId();
		this.setIntentToEdit();
		this.loading = false;
	}

	public async saveIntent() {
		this.$v.$touch();
		if (this.$v.$invalid) {
			Toast.showError('Fix the errors in red before saving');
			return;
		}
		try {
			if (this.nlpEnabled()) {
				this.isSaveInProgress = true;
				this.selectedIntent.utterances = this.selectedIntentUtterances.sort((uttOne, uttTwo) => uttOne.text.localeCompare(uttTwo.text));
				this.selectedIntent.title = this.selectedIntentTitle.trim();

				const intent = {
					utterances: this.selectedIntent.utterances.map(utterance => utterance.text),
					title: this.selectedIntent.title
				};
				await this.updateIntent({ id: this.selectedIntentId, intent });

				Toast.showSuccess('Saved intent');
				this.$emit('completed', this.selectedIntent);

				this.isSaveInProgress = false;
				this.closeModal();
			} else {
				Toast.showError('Unable to save intent - your workspace must have Natural Language support enabled');
				this.closeModal();
			}
		} catch (ex) {
			Toast.showError(ex.message);
			this.isSaveInProgress = false;
		}
	}

	public getErrorMessage(): string {
		if (this.$v.selectedIntentTitle.required && !this.$v.selectedIntentTitle.intentNameValid) {
			return 'Name must only contain letters, numbers, spaces, and the characters , - _ ( )';
		}
		if (!this.$v.selectedIntentTitle.maxLength) {
			return 'Name must not be longer than 50 characters';
		}
		if (!this.$v.selectedIntentTitle.required) {
			return 'Name must not be empty';
		}

		return '';
	}

	public isUnknownIntent(): boolean {
		return this.selectedIntentId === 'None';
	}

	private async loadIntentWithId() {
		try {
			this.selectedIntent = await this.getIntent(this.intentId);
		} catch (e) {
			this.errorLoading('Error loading intent');
		}
	}

	private errorLoading(message: string) {
		Toast.showError(message);
		this.show = false;
	}

	private setIntentToEdit() {
		if (this.selectedIntent) {
			this.selectedIntentId = this.selectedIntent.id!;
			this.selectedIntentUtterances = this.selectedIntent.utterances.sort((uttOne, uttTwo) => uttOne.text.localeCompare(uttTwo.text));
			this.selectedIntentTitle = this.selectedIntent.title;
		}
	}

	private nlpEnabled() {
		return this.isWorkspaceFeatureEnabled(NLP_ENABLED);
	}

	private closeModal() {
		this.show = false;
		this.selectedIntentId = '';
		this.selectedIntentTitle = '';
		this.selectedIntentUtterances = [];
	}
}
